import type {
  AdvertisingRow,
  AdvertisingValues,
} from '@borradh-workspace/api-client/types';
import type { AdMetric } from './advertising-metrics';

export type GroupBy = 'ads' | 'organizations' | 'campaigns';
export type { AdvertisingRow } from '@borradh-workspace/api-client/types';
export type Sort = { id: string; direction: 'asc' | 'desc' };
export const VALUE_KEYS: (keyof AdvertisingValues)[] = [
  'spend',
  'impressions',
  'reach',
  'clicks',
  'linkClicks',
  'landingViews',
  'conversations',
  'replied',
  'bookings',
  'furtherClicks',
  'conversions',
  'leads',
  'metaConversations',
  'engagements',
  'reactions',
  'comments',
  'shares',
  'plays',
  'plays3s',
  'thruPlays',
  'video25',
  'video50',
  'video75',
  'video100',
];

export function sumValues(ads: AdvertisingValues[]): AdvertisingValues {
  const values = Object.fromEntries(
    VALUE_KEYS.map((key) => {
      const available = ads
        .map((ad) => ad[key])
        .filter((value): value is number => value !== null);
      return [
        key,
        available.length ? available.reduce((a, b) => a + b, 0) : null,
      ];
    })
  ) as Record<keyof AdvertisingValues, number | null>;
  return {
    ...values,
    conversations: values.conversations ?? 0,
    replied: values.replied ?? 0,
    bookings: values.bookings ?? 0,
    furtherClicks: values.furtherClicks ?? 0,
    conversions: values.conversions ?? 0,
    reach: ads.length === 1 ? ads[0].reach : null,
  };
}

const csvCell = (value: string | number | null) => {
  if (value === null) return '';
  const safe =
    typeof value === 'string' && /^[=+\-@\t\r]/.test(value)
      ? `'${value}`
      : String(value);
  return `"${safe.replaceAll('"', '""')}"`;
};

export function buildCsv(
  rows: AdvertisingRow[],
  metrics: AdMetric[],
  since: string,
  until: string
) {
  const lines: (string | number | null)[][] = [
    [
      'From',
      'To',
      'Name',
      'Organization',
      'Currency',
      'Conversion measure',
      'Ad count',
      'Spend (EUR)',
      ...metrics.map(
        (metric) =>
          `${metric.name} (${metric.source}${metric.format === 'percent' ? ', %' : ''})`
      ),
    ],
    ...rows.map((row) => [
      since,
      until,
      row.name,
      row.organization,
      row.currency,
      row.conversionKind === 'booking' ? 'Borradh booking' : 'Further click',
      row.adCount,
      row.spendEur,
      ...metrics.map((metric) => metric.value(row)),
    ]),
  ];
  return `\uFEFF${lines.map((line) => line.map(csvCell).join(',')).join('\r\n')}`;
}
